/**
 * HealthPanel — HEALTH biome surface. The platform's 5 MCP workers don't (yet)
 * carry health, so this probes the health-biome service on MILLSTONE (:8480,
 * Google Fit + Samsung Health) via the Rust http_get command and renders
 * whatever metrics it returns. When the biome is offline (a known open infra
 * item) it degrades to a clear, honest scaffold — never silent failure.
 */
import { useCallback, useEffect, useState } from "react";
import { HeartPulse, RefreshCw, Moon, Footprints, Activity, Flame, AlertTriangle } from "lucide-react";
import { PanelScroll } from "./ClientsPanel";
import { isTauri } from "../lib/settings";

const BASE_KEY = "sage_health_base";
const DEFAULT_BASE = "http://192.168.8.180:8480";

interface Probe { ok: boolean; data?: Record<string, unknown>; error?: string }

const num = (o: Record<string, unknown>, ...keys: string[]): number | undefined => {
  for (const k of keys) { const v = o[k]; if (typeof v === "number") return v; if (typeof v === "string" && v.trim() && !Number.isNaN(Number(v))) return Number(v); }
  return undefined;
};

export default function HealthPanel() {
  const [probe, setProbe] = useState<Probe | null>(null);
  const [loading, setLoading] = useState(true);
  const base = (() => { try { return localStorage.getItem(BASE_KEY) || DEFAULT_BASE; } catch { return DEFAULT_BASE; } })();

  const run = useCallback(async () => {
    setLoading(true);
    if (!isTauri()) { setProbe({ ok: false, error: "desktop-only" }); setLoading(false); return; }
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      // Probe candidate endpoints in parallel; first JSON 2xx wins. 3.5s cap each
      // so an unreachable biome fails fast instead of stacking 5 timeouts.
      const paths = ["/summary", "/api/summary", "/health/summary", "/today"];
      const tries = paths.map(async (path) => {
        const r = await invoke<{ status: number; body: string }>("http_get", { url: `${base}${path}`, timeoutMs: 3500 });
        if (r.status >= 200 && r.status < 300) { const j = JSON.parse(r.body); if (j && typeof j === "object") return j as Record<string, unknown>; }
        throw new Error(`HTTP ${r.status}`);
      });
      const results = await Promise.allSettled(tries);
      const hit = results.find((r) => r.status === "fulfilled") as PromiseFulfilledResult<Record<string, unknown>> | undefined;
      if (hit) setProbe({ ok: true, data: hit.value });
      else setProbe({ ok: false, error: (results.find((r) => r.status === "rejected") as PromiseRejectedResult | undefined)?.reason?.message ?? "no response" });
    } catch (e) {
      setProbe({ ok: false, error: e instanceof Error ? e.message : String(e) });
    }
    setLoading(false);
  }, [base]);

  useEffect(() => { void run(); }, [run]);

  const d = probe?.data ?? {};
  const steps = num(d, "steps", "step_count", "stepsToday");
  const sleep = num(d, "sleep_hours", "sleepHours", "sleep");
  const hr = num(d, "resting_hr", "restingHeartRate", "heart_rate", "hr");
  const cals = num(d, "calories", "active_calories", "kcal");

  return (
    <PanelScroll accentVar="--biome-health" title="Health" subtitle="Google Fit + Samsung Health · health-biome :8480">
      <div className="flex items-center gap-2 -mt-1">
        <button onClick={() => void run()} className="flex items-center gap-1.5 px-3 h-8 rounded-full text-body-sm border bg-surface text-text-muted hover:text-text hover:border-primary/40 transition-colors">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Probe biome
        </button>
        <span className="text-caption text-text-dim font-mono">{base}</span>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-24 rounded-xl" />)}</div>
      ) : probe?.ok ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Metric icon={<Footprints size={18} />} label="Steps" value={steps != null ? steps.toLocaleString() : "—"} />
          <Metric icon={<Moon size={18} />} label="Sleep" value={sleep != null ? `${sleep.toFixed(1)}h` : "—"} />
          <Metric icon={<HeartPulse size={18} />} label="Resting HR" value={hr != null ? `${Math.round(hr)} bpm` : "—"} />
          <Metric icon={<Flame size={18} />} label="Active kcal" value={cals != null ? cals.toLocaleString() : "—"} />
        </div>
      ) : (
        <div className="rounded-xl border bg-surface p-5" style={{ borderLeft: "3px solid var(--biome-health)" }}>
          <div className="flex items-center gap-2 text-warning mb-2"><AlertTriangle size={16} /><span className="text-body-sm font-medium">Health biome offline</span></div>
          <p className="text-body-sm text-text-muted leading-relaxed">
            Couldn't reach <span className="font-mono text-text-dim">{base}</span>{probe?.error ? <> ({probe.error})</> : null}. The HEALTH biome
            (Google Fit live → :8480 + Samsung Health hook) is on the activation list — once health-biome is running on MILLSTONE this panel fills
            in automatically (steps, sleep, resting HR, active calories). Set a different base in <span className="font-mono">localStorage.sage_health_base</span> if it moved.
          </p>
          <div className="mt-3 flex items-center gap-2 text-caption text-text-dim"><Activity size={13} /> Scaffold ready · no live metrics yet</div>
        </div>
      )}
    </PanelScroll>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-surface p-4" style={{ borderLeft: "3px solid var(--biome-health)" }}>
      <div className="flex items-center gap-2 text-text-muted mb-2">{icon}<span className="text-caption uppercase tracking-wide">{label}</span></div>
      <div className="text-display font-serif text-text">{value}</div>
    </div>
  );
}
